// Client HTTP central de cc-jaw.
// - Prefixe l'URL de base de l'API (meme source que getEntraConfig().apiBaseUrl).
// - Injecte le Bearer token si fourni.
// - INTERCEPTEUR 401 : sur reponse 401 d'un appel authentifie, declenche le
//   handler global (enregistre par AuthContext) -> session videe + redirection
//   vers l'authentification. C'est le filet de securite cote frontend ;
//   la vraie protection reste le 401 du backend.

import { getEntraConfig } from '../auth/entraConfig';

export class ApiError extends Error {
  constructor(public status: number, public body?: unknown) {
    super(`API ${status}`);
  }
}

/** Erreur réseau / service injoignable / timeout (status 0). */
export class NetworkError extends Error {
  constructor(public reason: 'timeout' | 'network', public cause?: unknown) {
    super(`Network ${reason}`);
  }
}

/** Délai max par défaut d'une requête (ms) avant abandon -> NetworkError('timeout').
 *  30 s pour absorber un COLD START de l'ACA (scale-to-zero) sans timeout. */
export const DEFAULT_TIMEOUT_MS = 30000;

/** fetch avec timeout (AbortController) + retry sur cold start.
 *  Lève NetworkError sur timeout/coupure. Le retry n'est fait que pour les
 *  méthodes IDEMPOTENTES (GET/HEAD) : jamais sur POST/PUT (ex. buy) pour éviter
 *  un double effet de bord si la requête a en fait atteint le serveur. */
export async function fetchWithTimeout(
  input: string,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<Response> {
  const method = (init.method || 'GET').toUpperCase();
  const maxRetries = method === 'GET' || method === 'HEAD' ? 1 : 0;
  let lastErr: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(input, { ...init, signal: controller.signal });
    } catch (e: any) {
      lastErr = new NetworkError(controller.signal.aborted ? 'timeout' : 'network', e);
      if (attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, 800)); // laisse le conteneur démarrer
        continue;
      }
    } finally {
      clearTimeout(timer);
    }
  }
  throw lastErr;
}

let onUnauthorized: () => void = () => {};
/** Enregistre le handler appele sur 401 (typiquement par AuthContext). */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}
/** Declenche manuellement l'intercepteur 401 (pour les appels fetch directs). */
export function notifyUnauthorized(): void {
  onUnauthorized();
}

// --- Indicateur de chargement GLOBAL -------------------------------------
// Compteur de requetes en vol : toute requete (apiFetch + fetchs des contexts
// via beginRequest/endRequest) incremente/decremente ce compteur. Un overlay
// global (GlobalLoadingIndicator) s'affiche tant qu'au moins une requete tourne.
type LoadingListener = (active: boolean) => void;
let _inFlight = 0;
const _loadingListeners = new Set<LoadingListener>();

function _emitLoading(): void {
  const active = _inFlight > 0;
  _loadingListeners.forEach((l) => l(active));
}

/** S'abonne a l'etat de chargement global. Retourne une fonction de desabonnement. */
export function subscribeLoading(listener: LoadingListener): () => void {
  _loadingListeners.add(listener);
  listener(_inFlight > 0);
  return () => {
    _loadingListeners.delete(listener);
  };
}

/** Signale le debut d'une requete (a appairer avec endRequest dans un finally). */
export function beginRequest(): void {
  _inFlight += 1;
  _emitLoading();
}

/** Signale la fin d'une requete. */
export function endRequest(): void {
  _inFlight = Math.max(0, _inFlight - 1);
  _emitLoading();
}

/** Enrobe une promesse pour qu'elle alimente l'indicateur global. */
export async function trackRequest<T>(p: Promise<T>): Promise<T> {
  beginRequest();
  try {
    return await p;
  } finally {
    endRequest();
  }
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  token?: string | null;
  /** true => appel authentifie : un 401 declenche l'intercepteur. */
  auth?: boolean;
  /** Délai max avant abandon (ms). Défaut: DEFAULT_TIMEOUT_MS. */
  timeoutMs?: number;
}

export async function apiFetch<T = any>(path: string, opts: ApiOptions = {}): Promise<T> {
  const base = getEntraConfig().apiBaseUrl.replace(/\/$/, '');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.token) headers['Authorization'] = `Bearer ${opts.token}`;

  beginRequest();
  try {
    const res = await fetchWithTimeout(
      `${base}${path}`,
      {
        method: opts.method || 'GET',
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      },
      opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    );

    if (res.status === 401) {
      if (opts.auth) onUnauthorized();
      throw new ApiError(401);
    }

    const text = await res.text();
    let data: any = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    if (!res.ok) throw new ApiError(res.status, data);
    return data as T;
  } finally {
    endRequest();
  }
}
